/**
 * The Drops screen's decisions, as testable logic.
 *
 * Second migration of the six screens that predate the write pattern
 * (atomic#12), after SendOneCard — taken next because this is the other money
 * path: `plan_drop` turns a cell into cards and the send button buys postage.
 *
 * See `marketing/CLAUDE.md`, "Writes that are not plain CRUD", for why the RPC
 * is a parameter here rather than a method on the data provider.
 *
 * Input types are NARROW on purpose — a rule that needs four fields should not
 * make a test build all seventeen of `Row`.
 */

/* ── the RPC seam ──────────────────────────────────────────────────────── */

/** Drops calls several functions through one shape, so the name is an argument. */
export type DropRpc = (
  fn: string,
  args: Record<string, unknown>,
) => Promise<unknown>;

/** The real one. Lazily imported so a test that never writes needs no client. */
export const dropRpcViaSupabase: DropRpc = async (fn, args) => {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  const { data, error } = await getSupabaseClient().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
};

/* ── planning ──────────────────────────────────────────────────────────── */

/**
 * Read `plan_drop`'s count.
 *
 * Same defect SendOneCard carried, and the same fix. The inline form was
 *
 *     const r = (Array.isArray(res) ? res[0] : res) as { planned: number };
 *
 * which on an empty array reads `undefined.planned` and shows whoever pressed
 * **Plan** a toast saying "Cannot read properties of undefined". Here it is
 * worse than on one card: this is the number of cards a person is about to buy
 * postage for, so a wrong or absent value must stop rather than display.
 */
export function readPlannedCount(res: unknown): number {
  const row = Array.isArray(res) ? res[0] : res;
  if (row === null || row === undefined) {
    throw new Error("plan_drop returned no row — nothing was planned.");
  }
  const n = (row as { planned?: unknown }).planned;
  if (typeof n !== "number" || !Number.isFinite(n)) {
    throw new Error(
      `plan_drop returned no usable count (got ${JSON.stringify(n)}).`,
    );
  }
  return n;
}

export type PlanTarget = {
  cell: string;
  touch: string;
  vendor: string | null;
};

/**
 * Plan a drop, or count what planning it would do.
 *
 * `dryRun` reaches the function as `p_dry_run` and is what makes the preview
 * safe: the same code path, the same refusals, no rows written.
 */
export async function planDrop(
  rpc: DropRpc,
  target: PlanTarget,
  dryRun: boolean,
): Promise<number> {
  // plan_drop raises for HOLDOUT, a malformed touch or an unknown cell.
  // Surfacing the database's own message keeps ONE explanation rather than a
  // paraphrase that drifts from it.
  const res = await rpc("plan_drop", {
    p_cell: target.cell,
    p_touch: target.touch,
    p_dry_run: dryRun,
    p_vendor: target.vendor,
  });
  return readPlannedCount(res);
}

/** Remove planned cards for one cell and touch. Anything sent is kept. */
export async function unplanDrop(
  rpc: DropRpc,
  cell: string,
  touch: string,
): Promise<unknown> {
  return rpc("unplan_drop", { p_cell: cell, p_touch: touch });
}

/* ── removing a whole drop ─────────────────────────────────────────────── */

export type RemovableDrop = {
  mailings_planned: number;
  mailings_sent: number;
  schedule_id: number | null;
};

export type RemovalPlan =
  | { refuse: string }
  | { unplan: boolean; deleteSchedule: boolean };

/**
 * Narrow a plan to its refusal.
 *
 * A discriminant rather than a truthiness check on `refuse`: `refuse: string`
 * includes the empty string, so `if (plan.refuse)` leaves TypeScript unable to
 * rule the refusal out on the other branch — and a refusal that reads as
 * "proceed" is the one mistake this type exists to prevent.
 */
export function isRefusal(plan: RemovalPlan): plan is { refuse: string } {
  return "refuse" in plan;
}

/**
 * What removing this drop should actually do — decided before anything runs.
 *
 * THE REFUSAL IS THE POINT. A drop with sent cards is history: postage was
 * bought and paper is in the post, and no screen action can un-send it. Making
 * that a value rather than an early `return` inside a click handler is what
 * lets it be asserted without a browser.
 *
 * The two halves are independent because a drop can exist as either: planned
 * rows with no schedule row, or a schedule row with nothing planned yet. The
 * board is a FULL OUTER JOIN precisely so both show up, so removal has to cope
 * with both.
 */
export function planRemoval(row: RemovableDrop): RemovalPlan {
  if (row.mailings_sent > 0) {
    return {
      refuse:
        "Cards have already been sent for this drop; history is not editable.",
    };
  }
  return {
    unplan: row.mailings_planned > 0,
    deleteSchedule: row.schedule_id !== null,
  };
}

/* ── why a drop cannot be sent ─────────────────────────────────────────── */

export type BlockableDrop = {
  touch: string;
  mailings_planned: number;
  creative_name: string | null;
  artwork_ready: boolean | null;
};

/**
 * The single next obstacle, in the order the sender hits them.
 *
 * ONE REASON AT A TIME, and the order is the rule. A list of four problems
 * reads as "this is broken"; one reads as "do this next". The sequence follows
 * the actual workflow — there is no point naming missing artwork to somebody
 * who has not planned any cards yet.
 */
export function blockedReason(row: BlockableDrop): string | null {
  if (row.mailings_planned === 0) return "Nothing is planned yet";
  if (!row.creative_name) return `No creative exists for ${row.touch}`;
  if (!row.artwork_ready) return `${row.touch} is missing artwork for one side`;
  return null;
}

/* ── scheduling ────────────────────────────────────────────────────────── */

/**
 * Turn a failed schedule into something a person can act on.
 *
 * A unique-violation is the one failure with an obvious human meaning, and
 * Postgres' own text for it ("duplicate key value violates unique constraint
 * drop_schedule_cell_touch_key") names a constraint nobody outside this repo
 * has heard of. Every other error keeps its own message, because a paraphrase
 * of an unknown failure is worse than the failure.
 */
export function scheduleErrorMessage(
  err: unknown,
  cell: string,
  touch: string,
): string {
  const m = String((err as Error)?.message ?? err);
  return /duplicate|unique/i.test(m)
    ? `${cell} is already scheduled for ${touch}.`
    : m;
}

/* ── grouping the board ────────────────────────────────────────────────── */

export type DatedDrop = { planned_on: string | null };

/**
 * Group drops by date, with undated ones FIRST.
 *
 * The question the screen answers is "what goes out that week", so dates
 * ascend. Undated drops lead rather than being hidden or sorted last: a drop
 * with no date is a gap somebody should see, not a reason to omit the row.
 * Hiding it is how a cell reaches the end of a campaign unmailed.
 */
export function groupDropsByDate<T extends DatedDrop>(
  rows: readonly T[],
): ReadonlyArray<readonly [string, T[]]> {
  const byDate: Record<string, T[]> = {};
  for (const r of rows) {
    (byDate[r.planned_on ?? "unscheduled"] ??= []).push(r);
  }
  const dates = Object.keys(byDate)
    .filter((d) => d !== "unscheduled")
    .sort();
  return [
    ...(byDate.unscheduled
      ? [["unscheduled", byDate.unscheduled] as const]
      : []),
    ...dates.map((d) => [d, byDate[d]] as const),
  ];
}

/* ── the manifest CSV ──────────────────────────────────────────────────── */

/**
 * RFC 4180 quoting.
 *
 * An addressee like `LOGAN VINEYARDS, LLC` contains a comma and would
 * otherwise split into two columns at the mail house — silently, and only
 * visible once the cards are printed wrong.
 */
export function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The columns the mail house reads, in the order it expects them. */
export const MANIFEST_COLUMNS = [
  "recipient_ref",
  "addressee",
  "street",
  "city",
  "state",
  "zip5",
] as const;

export type ManifestCsvRow = Partial<
  Record<(typeof MANIFEST_COLUMNS)[number], unknown>
>;

/** Header row plus one line per recipient. A null manifest yields the header. */
export function buildManifestCsv(
  manifest: readonly ManifestCsvRow[] | null,
): string {
  return [
    MANIFEST_COLUMNS.join(","),
    ...(manifest ?? []).map((r) =>
      MANIFEST_COLUMNS.map((c) => csvCell(r[c])).join(","),
    ),
  ].join("\n");
}

export function manifestFilename(cell: string, touch: string): string {
  return `barrel-scout-${cell}-${touch}.csv`;
}
